import {Injectable} from '@angular/core';
import {ConnectorService} from './connector.service';
import {from, map, Observable, of, switchMap} from 'rxjs';
import {CreditLetterModel, PaymentTransactionModel} from '../../models/contract.model';
import {FinanceProfileModel, FinanceWalletModel, ForecastModel} from '../../models/finance.model';
import {HelpersService} from '../helpers.service';
import {MemberService} from '../member.service';

@Injectable({
    providedIn: 'root'
})
export class FinanceService {
    constructor(
        private helper: HelpersService,
        private memberService: MemberService,
        private connectService: ConnectorService
    ) {
    }

    /**
     * Финансовый профиль проекта
     */
    getFinance(): Observable<{ purse: FinanceProfileModel }> {
        return this.connectService.get('instance/eft/purse/project/profile');
    }

    /**
     * Получение кошелька по GUID
     * @param guid
     */
    getWalletByGuid(guid: string): Observable<{ wallet: FinanceWalletModel }> {
        return this.connectService.get('instance/eft/purse/project/wallet/' + guid);
    }

    /**
     * Создание нового кошелька
     * @param title
     */
    createWallet(title: string): Observable<{ wallet: FinanceWalletModel }> {
        return this.connectService.post('instance/eft/purse/project/wallet', {
            title,
            currency_code: 'RUB'
        });
    }

    /**
     * История платежей
     */
    getPaymentsHistory(guid: string | null = null, paymentType = 'ALL'): Observable<{
        payments: {
            current_page: number,
            page_count: number,
            total: number,
            data: PaymentTransactionModel[]
        }
    }> {
        let args = {'payment_type': paymentType}
        if (guid) {
            args['guid'] = guid;
        }
        return this.connectService.get('instance/eft/purse/payments', args);
    }

    /**
     * История платежей по датам
     */
    getPaymentsBetweenDates(startDate: string, endDate: string, paymentType = 'ALL'): Observable<{
        payments: {
            current_page: number,
            page_count: number,
            total: number,
            data: PaymentTransactionModel[]
        }
    }> {
        const args = {
            'payment_type': paymentType,
            'start_date': startDate,
            'end_date': endDate
        }
        return this.connectService.get('instance/eft/purse/payments', args);
    }

    /**
     * Платеж по guid
     */
    getPayment(guid: string): Observable<{ transaction: PaymentTransactionModel }> {
        return this.connectService.get('instance/eft/purse/transaction/' + guid);
    }

    /**
     * Создание транзакции на пополнение
     */
    createReplenishmentTransaction(walletGuid: string, total: number, recipientType: string): Observable<{
        transaction: PaymentTransactionModel
    }> {
        return this.connectService.post('instance/eft/purse/replenishment', {
            recipient_type: recipientType,
            wallet_guid: walletGuid,
            total
        });
    }

    /**
     * Создание транзакции на вывод
     */
    createPayoutTransaction(walletGuid: string, total: number, bankAccount: string): Observable<{
        transaction: PaymentTransactionModel
    }> {
        return this.connectService.post('instance/eft/purse/payout', {
            wallet_guid: walletGuid,
            total,
            bank_account: bankAccount
        });
    }

    /**
     * Перевод между кошельками
     */
    createBarterTransaction(
        outWalletGuid: string,
        inWalletGuid: string,
        walletType: string,
        total: number
    ): Observable<{ transaction: PaymentTransactionModel }> {
        return this.connectService.post('instance/eft/purse/barter', {
            from_wallet_guid: outWalletGuid,
            to_wallet_guid: inWalletGuid,
            wallet_type: walletType,
            total
        });
    }

    /**
     * Получение аккредитива по guid
     * @param creditLetterGuid
     */
    getCreditLetter(creditLetterGuid: string): Observable<{ credit_letter: CreditLetterModel }> {
        return this.connectService.get('instance/eft/purse/credit-letter/' + creditLetterGuid).pipe(
            switchMap(data =>
                data.credit_letter.owner_type === 'MEMBER' ?
                    from(this.memberService.memberGuidsHandler([data.credit_letter.owner_guid]))
                        .pipe(map(users => {
                            data.credit_letter.owner_name = this.helper.fullName(users[0]);
                            return data;
                        }))
                    : of(data))
        );
    }

    /**
     * Удаление кошелька
     * @param walletGuid
     * @returns
     */
    deleteWallet(walletGuid: string): Observable<{ message: string }> {
        return this.connectService.delete('instance/eft/purse/project/wallet/' + walletGuid);
    }

    /**
     * Информация доступности средств по всем кошелькам
     */
    getMoneyInfo(): Observable<{ forecast: { forecast_total: ForecastModel, forecast_wallets: ForecastModel[] } }> {
        return this.connectService.get('instance/eft/purse/project/wallets-forecast');
    }

    /**
     * Получение мультиплатежа из степа контракта
     * @param multiPaymentGuid
     */
    getMultiPayment(multiPaymentGuid: string): Observable<any> {
        return this.connectService.get('instance/eft/contract/roadmap/target/multi_payment/' + multiPaymentGuid);
    }

    /**
     * Подписание платежа
     * @param multiPaymentGuid
     * @param paymentGuid
     * @param pin
     */
    signPayment(multiPaymentGuid: string, paymentGuid: string, pin: string): Observable<any> {
        return this.connectService.post('instance/eft/contract/roadmap/target/multi_payment/' +
            multiPaymentGuid + '/' + paymentGuid + '/sign', {PIN_code: pin});
    }

    /**
     * Отзыв подписи платежа
     * @param multiPaymentGuid
     * @param paymentGuid
     * @param pin
     */
    terminatePayment(multiPaymentGuid: string, paymentGuid: string, pin: string): Observable<any> {
        return this.connectService.post('instance/eft/contract/roadmap/target/multi_payment/' +
            multiPaymentGuid + '/' + paymentGuid + '/cancel-sign', {PIN_code: pin});
    }

    /**
     * Подписание / Отзыв подписи обеспечения
     * @param creditLetterGuid
     * @param pin
     * @param sign:boolean true - подписание; false - отзыв подписи
     */
    signOnOffCreditLetter(creditLetterGuid: string, pin: string, sign: boolean, contractGuid: string): Observable<any> {
        return this.connectService.post('instance/eft/contract/roadmap/target/credit_letter/' +
            creditLetterGuid + (sign ? '/sign' : '/cancel-sign'), {PIN_code: pin, contract_guid: contractGuid});
    }

    /**
     * Создание личного кошелька
     * @param data {
     *     title: string
     *     currency_code: string  (например 'RUB')
     * }
     */
    createPrivateWallet(data: { title: string, currency_code: string }): Observable<{ wallet: FinanceWalletModel }> {
        return this.connectService.post('instance/eft/purse/private/wallet', data);
    }

    /**
     * Получение всех личных кошельков
     */
    getAllPrivateWallets(): Observable<{ wallets: FinanceWalletModel[] }> {
        return this.connectService.get('instance/eft/purse/private/wallet/all');
    }

    /**
     * Удаление личного кошелька по GUID
     * @param guid
     */
    deletePrivateWallet(guid: string): Observable<any> {
        return this.connectService.delete('instance/eft/purse/private/wallet/' + guid);
    }

    /**
     * Получение личного кошелька по GUID
     * @param guid
     */
    getPrivateWalletByGuid(guid: string): Observable<{ wallet: FinanceWalletModel }> {
        return this.connectService.get('instance/eft/purse/private/wallet/' + guid);
    }

    /**
     * Информация доступности средств по всем личным кошелькам
     */
    getForecastPrivateWallets(): Observable<{
        forecast: { forecast_total: ForecastModel, forecast_wallets: ForecastModel[] }
    }> {
        return this.connectService.get('instance/eft/purse/private/wallets-forecast');
    }
}
